import React, { useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../reusable-components/AuthContext';
import { CurrentPageContext } from '../reusable-components/CurrentPageContext';
import { checkTokenAndRenderCode } from '../utils/common-functions';
import { useInvoiceDownloaderAuth } from '../hooks/use-invoice-downloader-auth';
import { useInvoiceDownloaderDownload } from '../hooks/use-invoice-downloader-download';
import { InvoiceDownloaderLoginForm } from './invoice-downloader/InvoiceDownloaderLoginForm';
import { InvoiceDownloaderCaptchaForm } from './invoice-downloader/InvoiceDownloaderCaptchaForm';
import { InvoiceDownloaderDownloadForm } from './invoice-downloader/InvoiceDownloaderDownloadForm';
import { InvoiceDownloaderProgress } from './invoice-downloader/InvoiceDownloaderProgress';
import { InvoiceDownloaderDownloadButtons } from './invoice-downloader/InvoiceDownloaderDownloadButtons';
import { InvoiceDownloaderToastContainer } from './invoice-downloader/InvoiceDownloaderToastContainer';
import { InvoiceDownloaderActionButton } from './invoice-downloader/InvoiceDownloaderActionButton';

export const InvoiceDownloaderPage: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { token } = authContext;
  const currentPageContext = useContext(CurrentPageContext);
  const { setCurrentRoute } = currentPageContext;
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');

  const handleDownloadComplete = (sessionId: string) => {
    authHook.resetAuth(true, sessionId);
    downloadHook.resetDownload(true, sessionId);
  };

  const authHook = useInvoiceDownloaderAuth();
  const downloadHook = useInvoiceDownloaderDownload(
    authHook.sessionId,
    handleDownloadComplete,
  );

  // Helper function to handle async operations with error handling
  const handleAsyncOperation = async <T,>(
    operation: () => Promise<{ error?: string } | T>,
    onSuccess?: () => void,
  ) => {
    const result = await operation();
    if (result && typeof result === 'object' && 'error' in result) {
      if (result.error) {
        setError(result.error);
      }
    } else if (onSuccess) {
      onSuccess();
    }
  };

  const handleDownloadInvoices = async (e: React.FormEvent) => {
    await handleAsyncOperation(() =>
      downloadHook.handleDownloadInvoices(e, authHook.sessionId),
    );
  };

  const handleCancelDownload = async () => {
    await downloadHook.handleCancelDownload(authHook.sessionId);
    // Reset auth state to show login form, but preserve username/password
    // Skip closing browser since handleCancelDownload already does it
    await authHook.resetAuth(false, authHook.sessionId, true);
  };

  const resetAuthState = () => {
    authHook.setSessionId('');
    authHook.setAuthReady(false);
    authHook.setCaptchaImageBase64('');
    authHook.setCaptchaCode('');
    setError('');
  };

  const handleCleanupAllSessions = async (e: React.FormEvent) => {
    await handleAsyncOperation(
      () => downloadHook.handleCleanupAllSessions(e),
      resetAuthState,
    );
  };

  // Memoized computed values to avoid recalculation
  const combinedError = useMemo(
    () => error || authHook.error,
    [error, authHook.error],
  );
  const combinedLoading = useMemo(() => authHook.loading, [authHook.loading]);
  const isAuthReady = useMemo(
    () => authHook.authReady && !combinedLoading,
    [authHook.authReady, combinedLoading],
  );
  const showLoginForm = useMemo(
    () => authHook.sessionId === '',
    [authHook.sessionId],
  );
  const showActionButtons = useMemo(
    () => !authHook.authReady && !combinedLoading && !authHook.showCaptcha,
    [authHook.authReady, combinedLoading, authHook.showCaptcha],
  );

  // Helper function to create onChange handlers
  const createChangeHandler = (setter: (value: string) => void) => {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setter(e.target.value);
    };
  };

  const pageJSX: JSX.Element = (
    <div className="md:container md:mx-auto p-4">
      <InvoiceDownloaderToastContainer />

      <h1 className="text-2xl font-bold my-4">Vodafone Invoice Downloader</h1>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (isAuthReady) {
            handleDownloadInvoices(e);
          }
        }}
      >
        {showLoginForm && (
          <InvoiceDownloaderLoginForm
            username={authHook.username}
            password={authHook.password}
            loading={combinedLoading}
            onUsernameChange={createChangeHandler(authHook.setUsername)}
            onPasswordChange={createChangeHandler(authHook.setPassword)}
            onStartLogin={authHook.handleStartLogin}
          />
        )}

        {isAuthReady && (
          <InvoiceDownloaderDownloadForm
            invoices={downloadHook.invoices}
            onInvoicesChange={(e) =>
              downloadHook.setInvoices(parseInt(e.target.value))
            }
            fromYear={downloadHook.fromYear}
            onFromYearChange={(e) => downloadHook.setFromYear(e.target.value)}
            fromMonth={downloadHook.fromMonth}
            onFromMonthChange={(e) => downloadHook.setFromMonth(e.target.value)}
          />
        )}

        {authHook.showCaptcha && (
          <InvoiceDownloaderCaptchaForm
            captchaImageBase64={authHook.captchaImageBase64}
            captchaCode={authHook.captchaCode}
            loading={combinedLoading}
            onCaptchaCodeChange={createChangeHandler(authHook.setCaptchaCode)}
            onSendCaptcha={authHook.sendCaptcha}
          />
        )}

        <div className="max-w-4xl py-4 flex">
          {showActionButtons && (
            <InvoiceDownloaderActionButton
              disabled={!authHook.username || !authHook.password}
              loading={combinedLoading}
              loadingText="Processing..."
              onClick={authHook.handleStartLogin}
            >
              Start Login
            </InvoiceDownloaderActionButton>
          )}
          {isAuthReady && !downloadHook.downloadInProgress && (
            <InvoiceDownloaderActionButton
              type="submit"
              loading={combinedLoading}
              loadingText="Processing..."
            >
              Download Invoices
            </InvoiceDownloaderActionButton>
          )}
          {downloadHook.downloadInProgress && (
            <InvoiceDownloaderActionButton
              variant="error"
              loading={combinedLoading}
              onClick={handleCancelDownload}
            >
              Cancel Download
            </InvoiceDownloaderActionButton>
          )}
          {showActionButtons && (
            <div className="flex mb-4 space-x-4 w-80">
              &nbsp;
              <button
                data-testid="cleanup-all-sessions-button"
                className="btn btn-secondary"
                onClick={handleCleanupAllSessions}
              >
                Cleanup All Sessions
              </button>
            </div>
          )}
        </div>

        {downloadHook.downloadInProgress && downloadHook.downloadStatus && (
          <InvoiceDownloaderProgress
            downloadStatus={downloadHook.downloadStatus}
          />
        )}

        {downloadHook.downloadLink !== '' &&
          !downloadHook.downloadInProgress && (
            <InvoiceDownloaderDownloadButtons
              downloadLink={downloadHook.downloadLink}
              userSessionCounter={downloadHook.userSessionCounter}
              onDownloadZipFile={downloadHook.handleDownloadZipFile}
              onDownloadLogsFile={downloadHook.handleDownloadLogsFile}
            />
          )}
      </form>

      <div className="max-w-xs flex justify-center">
        {combinedLoading ? (
          <div className="loading loading-spinner loading-lg w-10"></div>
        ) : (
          <div className="text-error">{combinedError}</div>
        )}
      </div>
    </div>
  );

  return checkTokenAndRenderCode(token, pageJSX, setCurrentRoute, navigate);
};
